/* eslint-disable camelcase */
import { createContext, PropsWithChildren, useEffect, useState } from 'react'
import { useFormRecord } from './hooks/useFormRecord'
import { useRecords } from './hooks/useRecords'
import { AreaSelected } from '../../Auth/AuthReducer'
import { ContextProps, FuelResponse, GetMaxCapacityParams, Order, Params } from './types'
import { useStorage } from '../../../hooks/useStorage'

export const FuelContext = createContext({} as ContextProps)

export const FuelProvider = ({ children }: PropsWithChildren) => {
  const {
    form,
    handleOrderByType,
    handleResultTest,
    handleTakePhoto,
    handleValueNumber,
    handleResetForm,
    handleSetInitialValues,
    setStatusDetail,
  } = useFormRecord()

  const {
    records,
    initializerRecords,
    saveRecord,
    changeStateForm,
    deleteRecords,
    getDataByState,
    deleteObsoleteRecords,
    getRecordById,
  } = useRecords()

  const [hasData, setHasData] = useState(false)
  const [initialData, setInitialData] = useState<FuelResponse>()
  const [params, setParams] = useState<Params>({
    centers: [],
    suppliers: [],
    ship: {},
    orderStatus: [],
    resourseTypes: [],
    navalArtifact: {},
    plannedOrders: [],
  })

  const { isFirstRender } = useStorage({
    key: 'fuel',
    initialData: {
      initialData,
      records,
    },
    setDataFromStorage({ initialData, records }) {
      if (initialData) {
        handleInitialData(initialData)
      }
      if (records) {
        initializerRecords(records)
      }
    },
  })

  useEffect(() => {
    deleteObsoleteRecords()
  }, [records])

  useEffect(() => {
    if (
      form.orderNumber !== null &&
      (form.stateForm === null || form.stateForm === 'in-progress')
    ) {
      handleSaveRecord({ ...form, stateForm: 'in-progress' })
    }
  }, [form])

  const handleInitialData = (data: FuelResponse) => {
    isFirstRender.current = false
    setInitialData(data)

    setParams(() => {
      return {
        centers: data.centers,
        orderStatus: data.order_status,
        resourseTypes: data.resource_types,
        suppliers: data.suppliers,
        ship: Object.fromEntries(data.suppliers.map((supplier) => [supplier.id, supplier.ships])),
        navalArtifact: Object.fromEntries(
          data.centers.map((center) => [center.id, center.naval_artifacts]),
        ),
        plannedOrders: (data?.planned_orders ?? []).map((order) => ({
          id: order.id,
          label: order.label,
          navalArtifactId: order.id_naval_artifact,
          nominalValue: order.nominal_value,
          number: order.number,
          orderDate: order.order_date,
          statusId: order.id_status,
          statusDetail: order.status_detail,
        })),
      }
    })
    setHasData(true)
  }

  const handleSaveRecord = (record: Order) => {
    saveRecord({
      ...record,
      savedAt: record.savedAt ?? new Date().toISOString(),
    })
  }

  const getFuelRecords = (area: AreaSelected): Order[] => {
    if (area !== 'logistic') return []
    return getDataByState(null)
  }

  const setRecordByOrderNumber = (orderNumber: string) => {
    const record = records[orderNumber]
    if (record) {
      handleSetInitialValues(record)
    }
  }

  const getStatusForm = () => {
    const statusForm = Object.values(form.statusDetail ?? {})
    const orderStatus = statusForm
      .map(({ status, time }) => {
        const tempStatus = params.orderStatus.find((temp) => temp.name === status)
        if (tempStatus) {
          return {
            id_status: tempStatus?.id,
            timestamp: time,
          }
        }
        return null
      })
      .filter((item) => item !== null)
      .sort((a, b) => a.id_status - b.id_status)

    return orderStatus
  }

  const handleResetData = () => {
    setHasData(false)
  }

  const deleteRecordById = (id: string) => {
    deleteRecords([id])
  }

  const getMaxCapacityByArtifact = ({ centerId, artifactId }: GetMaxCapacityParams): number => {
    const { [centerId]: artifacts } = params.navalArtifact

    if (artifacts) {
      const artifact = artifacts.find((navalArtifact) => navalArtifact.id === artifactId)
      return artifact?.max_capacity ?? 0
    }

    return 0
  }

  return (
    <FuelContext.Provider
      value={{
        params,
        form,
        handleOrderByType,
        handleTakePhoto,
        handleValueNumber,
        handleResultTest,
        handleSaveRecord,
        handleResetForm,
        records,
        getFuelRecords,
        setRecordByOrderNumber,
        handleInitialData,
        getStatusForm,
        hasData,
        handleResetData,
        deleteRecordById,
        setStatusDetail,
        getMaxCapacityByArtifact,
        changeStateForm,
        getDataByState,
        getRecordById,
        deleteObsoleteRecords,
      }}
    >
      {children}
    </FuelContext.Provider>
  )
}
